import React from "react";
import { Descriptions, Collapse, Typography } from "antd";
import SheetDrawer from "../../../components/sheet/SheetDrawer";
import { IoAirplane } from "react-icons/io5";
import * as formats from "../../../utils/FormatingFunctions";
import { formatMoney, formatDOLS } from "../../../utils/FormatingFunctions";
import {
  deriveBookingMeta,
  getProviderLabel,
  getPaymentLabel,
} from "../lib/bookingMeta";

const { Text } = Typography;
const { Panel } = Collapse;

const getTravelerName = (t) => {
  if (typeof t?.name === "string") return t.name;
  const firstName = t?.firstName || t?.name?.firstName || "";
  const lastName = t?.lastName || t?.name?.lastName || "";
  return `${firstName} ${lastName}`.trim();
};

const getTicketTravelers = (booking) =>
  (booking?.travelerSummary && booking.travelerSummary.length > 0
    ? booking.travelerSummary
    : booking?.booking_reference?.data?.travelers) || [];

const formatAmount = (amount, currency) => {
  if (currency === "NGN") return formatMoney(amount);
  if (currency === "USD") return formatDOLS(amount);
  if (!currency) return "—";
  try {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const BookingDetailDrawer = ({ open, booking, onClose }) => {
  const cancellation = booking?.cancellation;
  const meta = deriveBookingMeta(booking);

  return (
    <SheetDrawer
      title="Flight booking details"
      open={open}
      onClose={onClose}
      width={800}
    >
      <Descriptions title="Summary" bordered column={1} size="small">
        <Descriptions.Item label="Tramango ID">
          {booking?.tramangoId}
        </Descriptions.Item>
        <Descriptions.Item label="Type">{booking?.type}</Descriptions.Item>
        <Descriptions.Item label="Status">{booking?.status}</Descriptions.Item>
        <Descriptions.Item label="Booking created">
          {formats.formatDateInfo(booking?.createdAt)} at{" "}
          {formats.formatTime(booking?.createdAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Customer email">
          {booking?.email}
        </Descriptions.Item>
        <Descriptions.Item label="Payment">
          {getPaymentLabel(booking)}
        </Descriptions.Item>
        <Descriptions.Item label="Itinerary provider">
          {getProviderLabel(booking) ?? "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Amount">
          {formatAmount(booking?.amount, booking?.currency)}
        </Descriptions.Item>
        <Descriptions.Item label="PNR">
          {meta.pnr ?? "Not ticketed"}
        </Descriptions.Item>
        <Descriptions.Item label="Amadeus order ID">
          {meta.amadeusOrderId ?? "—"}
        </Descriptions.Item>
      </Descriptions>

      <h4 style={{ marginTop: 24 }}>Travelers</h4>
      {getTicketTravelers(booking).map((t, index) => (
        <div key={index} style={{ marginBottom: 12 }}>
          <p>Name: {getTravelerName(t) || "N/A"}</p>
          <p>Title: {t.title || "N/A"}</p>
          <p>Gender: {t.gender || "N/A"}</p>
          <p>Date of Birth: {t.dateOfBirth || t.birthDate || "N/A"}</p>
          <p>Email: {t.email || t?.contact?.emailAddress || "N/A"}</p>
          <p>
            Phone:{" "}
            {t.phone ||
              (t.phones && t.phones[0]?.number) ||
              t?.contact?.phones?.[0]?.number ||
              "N/A"}
          </p>
        </div>
      ))}

      <h4>Itinerary</h4>
      {booking?.booking_reference?.data?.flightOffers?.map(
        (flightDetails, offerIndex) => (
          <div className="itinerary" key={offerIndex}>
            {flightDetails?.itineraries?.map((itinerary, itinIndex) => (
              <div key={itinIndex}>
                <div className="title">
                  <p>
                    Flights from{" "}
                    {formats.getAirportCity(
                      itinerary?.segments[0]?.departure?.iataCode,
                    )}{" "}
                    to{" "}
                    {formats.getAirportCity(
                      itinerary?.segments[itinerary?.segments?.length - 1]
                        ?.arrival?.iataCode,
                    )}
                  </p>
                </div>
                <div className="infos">
                  {itinerary?.segments?.map((segment, segIndex) => (
                    <section className="section" key={segment.id ?? segIndex}>
                      <div>
                        <h5>{segment?.airlineName}</h5>
                        <div>
                          {formats.getAirportCity(segment?.departure?.iataCode)} (
                          {segment?.departure?.iataCode})
                        </div>
                        <div>
                          {formats.formatDateInfo(segment?.departure?.at)} at{" "}
                          {formats.formatTime(segment?.departure?.at)}
                        </div>
                      </div>
                      <div className="flexcol">
                        <IoAirplane />
                        <div>{formats.formatFlightTimeNew(segment?.duration)}</div>
                      </div>
                      <div>
                        <div>
                          {formats.getAirportCity(segment?.arrival?.iataCode)} (
                          {segment?.arrival?.iataCode})
                        </div>
                        <div>
                          {formats.formatDateInfo(segment?.arrival?.at)} at{" "}
                          {formats.formatTime(segment?.arrival?.at)}
                        </div>
                      </div>
                    </section>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ),
      )}

      <h4 style={{ marginTop: 24 }}>Cancellation audit</h4>
      {cancellation ? (
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="State">
            {cancellation.state}
          </Descriptions.Item>
          <Descriptions.Item label="Scope">
            {cancellation.scope}
          </Descriptions.Item>
          <Descriptions.Item label="Cancelled by">
            {cancellation.by?.name} ({cancellation.by?.adminType})
          </Descriptions.Item>
          <Descriptions.Item label="Requested at">
            {cancellation.requestedAt
              ? new Date(cancellation.requestedAt).toLocaleString()
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Completed at">
            {cancellation.completedAt
              ? new Date(cancellation.completedAt).toLocaleString()
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Reason">
            {cancellation.reason}
          </Descriptions.Item>
          <Descriptions.Item label="Provider">
            {cancellation.provider ?? "N/A"} {cancellation.providerReference}
          </Descriptions.Item>
          <Descriptions.Item label="Attempts">
            {cancellation.attempts}
          </Descriptions.Item>
        </Descriptions>
      ) : booking?.cancelledBy ? (
        <p>Cancelled by (legacy record): {booking.cancelledBy}</p>
      ) : (
        <Text type="secondary">This booking has not been cancelled.</Text>
      )}

      {cancellation?.providerResponse && (
        <Collapse style={{ marginTop: 12 }}>
          <Panel header="Provider response" key="response">
            <pre>{JSON.stringify(cancellation.providerResponse, null, 2)}</pre>
          </Panel>
        </Collapse>
      )}
      {cancellation?.providerError && (
        <Collapse style={{ marginTop: 12 }}>
          <Panel header="Provider error" key="error">
            <pre>{JSON.stringify(cancellation.providerError, null, 2)}</pre>
          </Panel>
        </Collapse>
      )}

      <Collapse style={{ marginTop: 24 }}>
        <Panel header="Raw booking JSON" key="raw">
          <pre>{JSON.stringify(booking, null, 2)}</pre>
        </Panel>
      </Collapse>
    </SheetDrawer>
  );
};

export default BookingDetailDrawer;
